import type { ComputeResourceCodes } from '../../capabilities/compute';
import { buildDiskPricingWatchFields, buildVendorDeviceMetaWatchFields } from '../../components/vendorDevice';
import { resolvePricingResourceCodes, resolveWatchFieldsFromResourceConfigs } from '../../platform/pipeline-kernel/resolveResourceWatch';
import type { ResourceConfigEntry, ResourceType, SkuModuleMeta } from '../../platform/pipeline-kernel/types';

export const ciCpu: ResourceType = {
  requirement: '',
  code: 'vCPU',
  description: 'vCPU',
  unit: 'Core',
};

export const ciMemory: ResourceType = {
  requirement: '',
  code: 'Memory',
  description: 'Memory',
  unit: 'Gi',
};

export const ciGpu: ResourceType = {
  requirement: '',
  code: 'vGPU',
  description: 'vGPU',
  unit: 'Card',
};

export const ciBandwidth: ResourceType = {
  requirement: '',
  code: 'Bandwidth',
  description: '网络带宽',
  unit: 'Mbps',
};

export const ciSystemStorage: ResourceType = {
  requirement: '',
  code: 'SystemVolume',
  description: '系统盘',
  unit: 'Gi',
};

export const ciDataStorage: ResourceType = {
  requirement: '',
  code: 'DataVolume',
  description: '共享存储卷',
  unit: 'Gi',
};

export const ciComputeTemplates = { ciCpu, ciMemory, ciGpu, ciBandwidth, ciSystemStorage, ciDataStorage };

export const CI_COMPUTE_RESOURCE_CODES: ComputeResourceCodes = {
  cpu: ciCpu.code,
  memory: ciMemory.code,
  gpu: ciGpu.code,
  bandwidth: ciBandwidth.code,
};

export const CI_RESOURCE_CONFIGS: ResourceConfigEntry[] = [
  {
    ...ciCpu,
    watchFields: [
      'resource.cpu.request',
      'resource.cpu.physicalCards',
      'resource.cpu.physicalCores',
      'resource.cpu.logicalCores',
      'resource.cpu.pricing',
      ...buildVendorDeviceMetaWatchFields('resource.cpuPricing'),
    ],
    displayIds: ['cpu'],
  },
  {
    ...ciMemory,
    watchFields: ['resource.memory', ...buildVendorDeviceMetaWatchFields('resource.memoryPricing')],
    displayIds: ['memory'],
  },
  {
    ...ciGpu,
    watchFields: [
      'resource.gpu',
      'resource.gpu.vendor',
      'resource.gpu.device',
      'resource.gpu.number',
      'resource.gpu.vendorID',
      'resource.gpu.deviceID',
      'resource.gpuPricing',
    ],
    displayIds: ['gpu'],
  },
  {
    ...ciBandwidth,
    watchFields: ['resource.network.Bandwidth', 'resource.network.egressRate', 'resource.network.bandwidthPricing'],
    displayIds: ['bandwidth'],
  },
  {
    ...ciSystemStorage,
    watchFields: ['systemDiskSize', ...buildVendorDeviceMetaWatchFields('systemDiskPricing')],
    displayIds: ['systemDisk'],
  },
  {
    ...ciDataStorage,
    watchFields: ['volumes', ...buildDiskPricingWatchFields('volumes.0')],
    displayIdPrefix: 'claimTemplate',
  },
];

export const CI_SKU_MODULE_META: Record<string, SkuModuleMeta> = {
  baseInfo: {
    resource: false,
    presetCheckFields: ['name'],
  },
  network: {
    resource: false,
    presetCheckFields: ['resource.network.vpc.name'],
  },
  systemSetting: {
    resource: true,
    presetCheckFields: ['basic.image.key', 'systemDiskSize'],
    /** 下单：无预置镜像时可补选；有预置则 UI 锁删改。派生参数以 SKU 为底 merge */
    orderMutableFields: ['image.image'],
    quantityFields: ['systemDiskSize'],
    quantityPresetPaths: { systemDiskSize: ['systemDiskSize'] },
  },
  startupCommandArgs: {
    resource: false,
    presetCheckFields: ['basic.image.command', 'basic.image.startupArgs', 'basic.image.args'],
    orderListItemLock: true,
  },
  envVars: {
    resource: false,
    presetCheckFields: ['basic.image.env'],
    orderListItemLock: true,
  },
  service: {
    resource: false,
    presetCheckFields: ['resource.network.services'],
    orderListItemLock: true,
  },
  resource: {
    resource: true,
    presetCheckFields: ['resource.cpu', 'resource.memory', 'resource.gpu', 'resource.network.Bandwidth'],
    quantityFields: ['compute.cpu', 'compute.memory', 'compute.gpu', 'compute.bandwidth'],
    quantityPresetPaths: {
      'compute.cpu': ['resource.cpu'],
      'compute.memory': ['resource.memory'],
      'compute.gpu': ['resource.gpu'],
      'compute.bandwidth': ['resource.network.Bandwidth'],
    },
  },
  storage: {
    resource: true,
    presetCheckFields: ['volumes'],
    quantityFields: ['containerStorage.list'],
    quantityPresetPaths: { 'containerStorage.list': ['volumes'] },
  },
  dns: {
    resource: false,
    orderListItemLock: true,
    presetCheckFields: [
      'resource.network.dnsConfig.nameservers',
      'resource.network.dnsConfig.searches',
      'resource.network.dnsConfig.options',
    ],
  },
};

export function resolveCiResourceWatchFields(configs: ResourceConfigEntry[] = CI_RESOURCE_CONFIGS): string[] {
  return resolveWatchFieldsFromResourceConfigs(configs);
}

export function resolveCiPricingResourceCodes(configs: ResourceConfigEntry[] = CI_RESOURCE_CONFIGS): Set<string> {
  return resolvePricingResourceCodes(configs);
}
